import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, throwError, take } from 'rxjs';
import { SessionExpiredService } from '../session-expired/session-expired.service';
import { AuthService } from '../auth/auth.service';

/** URLs that should not trigger the session expired dialog (e.g. login endpoints) */
const EXCLUDED_URL_PATTERNS = ['/auth', '/login', '/forgot-password'];

/**
 * Checks if the request URL is an authentication endpoint that should be excluded.
 * @param url - The request URL to check
 * @returns True if the URL should be excluded from session expired handling
 */
function isAuthEndpoint(url: string): boolean {
  return EXCLUDED_URL_PATTERNS.some((pattern) => url.includes(pattern));
}

/**
 * Determines if the HTTP error is an explicit 401 Unauthorized response.
 * @param error - The HTTP error response
 * @returns True if the status is explicitly 401
 */
function isExplicit401(error: HttpErrorResponse): boolean {
  return error.status === 401;
}

/**
 * Determines if the HTTP error is an opaque CORS error from a known API endpoint.
 * This occurs when the backend returns an error without CORS headers, causing the browser
 * to block the response and report status 0. Could be a 401, 500, or any other server error.
 * @param error - The HTTP error response
 * @returns True if the error is likely an opaque CORS error from a known API
 */
function isOpaqueCorsError(error: HttpErrorResponse): boolean {
  return (
    error.status === 0 &&
    !!error.url &&
    (error.url.includes('execute-api') || error.url.includes('technoapes'))
  );
}

/**
 * HTTP interceptor that handles 401 Unauthorized responses.
 * Shows a session expired dialog to the user before redirecting to login.
 *
 * For explicit 401 responses, the dialog is shown immediately and the error is re-thrown.
 * For opaque CORS errors (status 0), the interceptor verifies the Cognito session
 * before deciding whether to show the session expired dialog. The error is only
 * propagated after the verification completes to avoid race conditions.
 *
 * Excludes authentication endpoints to avoid showing "session expired" on login failures.
 * @param req - The outgoing HTTP request
 * @param next - The next handler in the interceptor chain
 * @returns Observable of the HTTP event
 */
export function TokenWmsInterceptorFn(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const sessionExpiredService = inject(SessionExpiredService);
  const authService = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const currentUrl = router.url;
      const shouldHandle = !isAuthEndpoint(req.url) && !currentUrl.includes('/auth');

      if (!shouldHandle) {
        return throwError(() => error);
      }

      if (isExplicit401(error) || isOpaqueCorsError(error)) {
        // For both explicit 401 and opaque CORS errors (status 0):
        // Verify the actual session state with Cognito before showing the dialog.
        // This prevents false positives when the backend returns 401 for non-auth reasons
        // or when status 0 is caused by server errors without CORS headers.
        if (!navigator.onLine) {
          // Offline: this is a network issue, not session expiration
          return throwError(() => error);
        }

        // Use a detached subscription to verify Cognito session state.
        // checkAndRefreshToken() uses Cognito SDK callbacks (not HTTP),
        // so we subscribe independently to avoid capturing unrelated HTTP errors
        // from the interceptor's RxJS stream.
        authService
          .checkAndRefreshToken()
          .pipe(take(1))
          .subscribe({
            next: (session) => {
              if (!session || !session.isValid()) {
                sessionExpiredService.showSessionExpiredDialog();
              }
            },
            error: (cognitoError) => {
              // Only show dialog if the error is from Cognito, not a re-captured HTTP error
              if (navigator.onLine && !(cognitoError instanceof HttpErrorResponse)) {
                sessionExpiredService.showSessionExpiredDialog();
              }
            },
          });
      }

      return throwError(() => error);
    })
  );
}
